/*
  global SignInWithApple
*/
import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { noop, isBrowserIE } from 'helpers/GlobalHelper';
import { handleAnalytics, Events } from '@foxcorp/delta-web-analytics';
import { isValidEmail } from 'helpers/Account';
import OverlayFooter from 'components/OverlayFooter';
import Header from '../../../Header';
import Style from './SignIn.module.scss';
import SignInApple from '../../../SignInApple';
import GoogleSignInButton from '../../../GoogleSignInButton';
import { onConsolidatedEmailSignIn } from '../../helpers/ConsolidatedHelper';

const createAccountText = 'Create Account or Sign In';
const continueWithEmailText = 'Continue With Email';
const orContinueWithText = 'or continue with';

const SignIn = ({
  checkStatus,
  email,
  headerProps = {},
  language,
  onAppleSignIn,
  onAppleLinking,
  onClose,
  setAppleEmail,
  setAppleToken,
  setEmail,
  setExistingEmail,
  setIsAppleEmailShared,
  perks = [],
  renderPerkImage = noop,
  toggleCreatePassword = noop,
  utagAccountSource,
}) => {
  const dialogElement = useRef(null);
  let emailEntry = React.createRef();

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      setEmail(emailInput);
      onConsolidatedEmailSignIn({ checkStatus, email: emailInput, setExistingEmail });
    }
  };

  useEffect(() => {
    handleAnalytics(
      {
        event: Events.PROFILE_INITIATED,
        payload: {
          page_start_source: utagAccountSource,
        },
      },
      {
        event: Events.PROFILE_PROMPT_LANDING,
        payload: {
          pageName: 'profile prompt:landing',
          pageType: 'profile prompt',
        },
      },
    );
    if (dialogElement) dialogElement.current.focus();
    if (emailEntry) emailEntry.focus();
  }, [dialogElement]);

  const [isDisabled, setIsDisabled] = useState(true);
  const [emailInput, setEmailInput] = useState(email);

  useEffect(() => {
    if (isValidEmail(emailInput)) {
      return setIsDisabled(false);
    }

    return setIsDisabled(true);
  }, [emailInput]);

  return (
    <main className={Style.Perks} onKeyPress={handleKeyPress}>
      <Header {...headerProps} />
      <div className={Style.PerksContainer} ref={dialogElement} role="link" tabIndex="0">
        <h1>{createAccountText}</h1>
        <div className={Style.perksWrap}>
          {perks.map(perk => {
            const { icon, text } = perk;
            return (
              <div className={Style.perkContainer} key={`perk-${text}`}>
                <div className={Style.imageContainer}>
                  {renderPerkImage({
                    alt: text,
                    className: 'perkIcon',
                    src: icon,
                  })}
                </div>
                <div className={Style.perkText}>{text}</div>
              </div>
            );
          })}
        </div>
        <input
          className={classNames(Style.inputField)}
          placeholder="Email"
          type="text"
          name="signUpEmail"
          ref={input => {
            emailEntry = input;
          }}
          defaultValue={email || ''}
          onChange={e => setEmailInput(e.target.value)}
        />
        <div className={Style.buttonWrap}>
          <button
            className={classNames(Style.btnPrimary, isDisabled ? Style.invalidEmail : null)}
            onClick={() => {
              setEmail(emailInput);
              onConsolidatedEmailSignIn({ checkStatus, email: emailInput, setExistingEmail });
            }}
            disabled={isDisabled}
          >
            {continueWithEmailText}
          </button>
          <div className={Style.signInText}>{orContinueWithText}</div>
          <div className={Style.socialContainer}>
            {SignInWithApple && !isBrowserIE() && (
              <div>
                <SignInApple
                  onAppleSignIn={onAppleSignIn}
                  onAppleLinking={onAppleLinking}
                  setAppleEmail={setAppleEmail}
                  setAppleToken={setAppleToken}
                  setIsAppleEmailShared={setIsAppleEmailShared}
                  isConsolidatedSignUp
                />
                <div className={Style.iconText}>Apple</div>
              </div>
            )}
            <div>
              <GoogleSignInButton
                onClose={onClose}
                toggleCreatePassword={toggleCreatePassword}
                isConsolidatedSignUp
              />
              <div className={Style.iconText}>Google</div>
            </div>
          </div>
        </div>
      </div>
      <OverlayFooter language={language} />
    </main>
  );
};

export default SignIn;

SignIn.propTypes = {
  checkStatus: PropTypes.func,
  email: PropTypes.string,
  onAppleLinking: PropTypes.func,
  onAppleSignIn: PropTypes.func,
  onClose: PropTypes.func,
  setExistingEmail: PropTypes.func,
  showSignin: PropTypes.bool,
  headerProps: PropTypes.shape({}).isRequired,
  language: PropTypes.shape({}).isRequired,
  perks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  renderPerkImage: PropTypes.func.isRequired,
  setAppleEmail: PropTypes.func,
  setAppleToken: PropTypes.func,
  setIsAppleEmailShared: PropTypes.func,
  setEmail: PropTypes.func,
  toggleCreatePassword: PropTypes.func,
  utagAccountSource: PropTypes.string.isRequired,
};
